import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, History, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FixedTable, RowIconButton, SectionHeader, TextPanel, TextToggle, type FixedColumn } from '@/components/ui/FixedTable'
import { useExpandedRows } from '@/hooks/useExpandedRows'
import { Pill, StatusPill } from '@/components/ui/StatusPill'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RichTextEditor } from '@/components/ui/RichTextEditor'
import { RichHtml } from '@/components/ui/RichHtml'
import { richToPlain } from '@/lib/richText'
import { Toggle } from '@/components/ui/Toggle'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import {
  ProposalLinesService as Service,
  type CatalogLine, type CatalogLineItem, type CatalogMeta, type CatalogTier, type NameHistoryEntry,
} from '@/services/modules/ProposalLinesService'
import { ProposalCatalogNav } from './ProposalCatalogNav'

const money = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const pct = (v: number) => `${(v * 100).toFixed(2).replace('.', ',')}%`

type RenameTarget = { type: 'line' | 'tier' | 'item'; id: string; name: string; history: NameHistoryEntry[]; what: string }

/**
 * Catálogo de propostas → Linhas e coberturas.
 *
 * O que cada linha oferece no assistente de proposta: modalidades, coberturas,
 * texto do PDF, carência e texto por modalidade. Linha com problema some do
 * assistente — e o motivo aparece só aqui.
 */
export function SettingsProposalCatalogPage() {
  const { can } = useAuthority()
  const canCreate = can('settings.proposal_setup.create')
  const canEdit = can('settings.proposal_setup.edit')
  const canDelete = can('settings.proposal_setup.delete')

  const [lines, setLines] = useState<CatalogLine[]>([])
  const [meta, setMeta] = useState<CatalogMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [lineId, setLineId] = useState('')
  const [adding, setAdding] = useState('')
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<CatalogLineItem | null>(null)
  const [removing, setRemoving] = useState<CatalogLineItem | null>(null)
  const [renaming, setRenaming] = useState<RenameTarget | null>(null)
  // Texto da cobertura abre na própria linha (nada de texto corrido na tabela).
  const exp = useExpandedRows()

  const load = useCallback(() => Service.list()
    .then(r => {
      setLines(r.data); setMeta(r.meta)
      setLineId(cur => (cur && r.data.some(l => l.id === cur) ? cur : r.data[0]?.id ?? ''))
    })
    .catch(e => { toast.error({ title: 'Não foi possível carregar', description: extractApiError(e).message }) })
    .finally(() => setLoading(false)), [])
  useEffect(() => { void load() }, [load])

  const line = useMemo(() => lines.find(l => l.id === lineId) ?? null, [lines, lineId])
  const usage = (itemId: string) => meta?.proposalsByItem.find(p => p.itemId === itemId)?.total ?? 0

  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true)
    try {
      await fn()
      toast.success(ok)
      await load()
      return true
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
      return false
    } finally { setBusy(false) }
  }

  // Otimista: o switch muda na hora; volta se o servidor recusar.
  const [togglingId, setTogglingId] = useState<string | null>(null)
  async function setInsurance(item: CatalogLineItem, next: boolean) {
    if (!line) return
    const patch = (value: boolean) => setLines(ls => ls.map(l => l.id === line.id
      ? { ...l, items: l.items.map(x => x.id === item.id ? { ...x, insurance: value } : x) }
      : l))
    patch(next)
    setTogglingId(item.id)
    try {
      await Service.setInsurance(item.id, next)
      toast.success(next ? `"${item.name}" marcada como securitária.` : `"${item.name}" deixou de ser securitária.`)
    } catch (e) {
      patch(!next)
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally { setTogglingId(null) }
  }

  async function addItem() {
    if (!line || !adding) return
    if (await run(() => Service.addItem(line.id, adding), 'Cobertura incluída na linha.')) setAdding('')
  }

  async function move(index: number, dir: -1 | 1) {
    if (!line) return
    const ids = line.items.map(i => i.id)
    const j = index + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[index], ids[j]] = [ids[j], ids[index]]
    // Otimista: a ordem muda na hora; o servidor confirma em seguida.
    setLines(ls => ls.map(l => l.id === line.id ? { ...l, items: ids.map(id => l.items.find(i => i.id === id)!) } : l))
    try { await Service.reorder(line.id, ids) } catch (e) {
      toast.error({ title: 'Não foi possível reordenar', description: extractApiError(e).message })
      await load()
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  const tierCols: FixedColumn<CatalogTier>[] = [
    { key: 'name', header: 'Modalidade', width: 220, render: t => (
      <div className="py-1">
        <span className="flex flex-wrap items-center gap-1.5 font-medium text-gray-900">
          {t.name}
          {canEdit && <RenameButton onClick={() => setRenaming({ type: 'tier', id: t.id, name: t.name, history: t.nameHistory, what: 'a modalidade' })} />}
        </span>
        <HistoryNote history={t.nameHistory} />
      </div>
    ) },
    { key: 'who', header: 'Quem pode aderir', render: t => t.beneficiaries || <span className="text-gray-400">—</span> },
    { key: 'curve', header: 'Curva', width: 160, render: t => t.curve ?? <span className="text-gray-400">—</span> },
    { key: 'price', header: 'Preço cheio', width: 140, numeric: true, render: t => t.price ? money(t.price) : <span className="font-medium text-amber-700">sem preço</span> },
    { key: 'status', header: 'Status', width: 128, render: t => <StatusPill status={t.status} feminine /> },
  ]

  const itemCols: FixedColumn<CatalogLineItem>[] = line ? [
    ...(canEdit ? [{ key: 'order', header: 'Ordem', width: 88, render: (i: CatalogLineItem) => {
      const idx = line.items.findIndex(x => x.id === i.id)
      return (
        <div className="flex">
          <RowIconButton label={`Subir ${i.name}`} disabled={idx === 0} onClick={() => move(idx, -1)}><ArrowUp size={15} /></RowIconButton>
          <RowIconButton label={`Descer ${i.name}`} disabled={idx === line.items.length - 1} onClick={() => move(idx, 1)}><ArrowDown size={15} /></RowIconButton>
        </div>
      )
    } } as FixedColumn<CatalogLineItem>] : []),
    { key: 'name', header: 'Cobertura', render: i => {
      const sem = i.weights.every(w => w.weight <= 0)
      return (
        <div className="space-y-1 py-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className={`font-medium ${i.itemStatus === 'ATIVO' ? 'text-gray-900' : 'text-gray-500'}`}>{i.name}</span>
            {canEdit && <RenameButton onClick={() => setRenaming({ type: 'item', id: i.itemId, name: i.name, history: i.nameHistory, what: 'a cobertura (vale para todas as linhas)' })} />}
            {i.itemStatus !== 'ATIVO' && <StatusPill status={i.itemStatus} feminine />}
            {sem && i.itemStatus === 'ATIVO' && <Pill tone="amber">Sem peso</Pill>}
          </span>
          <HistoryNote history={i.nameHistory} />
          <TextToggle open={exp.isOpen(i.id)} onToggle={() => exp.toggle(i.id)}
            html={i.description || i.generalText}
            kind={i.description ? 'Texto desta linha' : 'Texto geral'}
            extra={i.tierTexts.length > 0 ? `${i.tierTexts.length} texto${i.tierTexts.length > 1 ? 's' : ''} por modalidade` : undefined} />
        </div>
      )
    } },
    { key: 'grace', header: 'Carência', width: 112, render: i => i.grace || <span className="text-gray-400">—</span> },
    { key: 'insurance', header: <span title="Cobertura de seguro: aparece marcada na proposta e no PDF">Securitária</span>, width: 112, align: 'center', render: i => (
      <div className="flex justify-center">
        <Toggle checked={i.insurance} disabled={!canEdit || togglingId === i.id} loading={togglingId === i.id}
          onChange={next => setInsurance(i, next)} />
      </div>
    ) },
    ...line.tiers.map((t): FixedColumn<CatalogLineItem> => ({
      key: `w-${t.id}`, header: <span title={`Peso em ${t.name}`}>{t.name}</span>, width: 104, numeric: true,
      render: i => {
        const w = i.weights.find(x => x.tierId === t.id)?.weight ?? 0
        return w > 0 ? pct(w) : <span className="text-gray-300">—</span>
      },
    })),
    { key: 'actions', header: <span className="sr-only">Ações</span>, width: 104, align: 'right', render: i => (
      <div className="flex justify-end">
        {canEdit && <RowIconButton label={`Editar textos de ${i.name}`} onClick={() => setEditing(i)}><Pencil size={15} /></RowIconButton>}
        {canDelete && <RowIconButton label={`Tirar ${i.name} da linha`} danger onClick={() => setRemoving(i)}><Trash2 size={15} /></RowIconButton>}
      </div>
    ) },
  ] : []

  const weightFooter = line ? [
    ...(canEdit ? [''] : []),
    'Soma dos pesos', '', '',
    ...line.tiers.map(t => pct(line.items.filter(i => i.itemStatus === 'ATIVO')
      .reduce((s, i) => s + (i.weights.find(x => x.tierId === t.id)?.weight ?? 0), 0))),
    '',
  ] : undefined

  return (
    <>
      <PageHeader
        title="Catálogo de propostas"
        subtitle="O que cada linha oferece no assistente de proposta"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Linhas e coberturas' }]}
      />
      <ProposalCatalogNav />

      {!meta?.activeTable && (
        <p className="mb-4 flex gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Nenhuma tabela de preço ativa: nenhuma linha aparece nas propostas. Importe ou ative uma em <Link to="/settings/commercial/proposal-pricing" className="font-semibold underline">Tabelas de preço</Link>.</span>
        </p>
      )}

      <div role="group" aria-label="Linha" className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {lines.map(l => {
          const on = l.id === lineId
          const nTiers = l.tiers.filter(t => t.status === 'ATIVO').length
          const nItems = l.items.filter(i => i.itemStatus === 'ATIVO').length
          return (
            <button key={l.id} type="button" onClick={() => setLineId(l.id)} aria-pressed={on}
              className={`flex cursor-pointer flex-col gap-1.5 rounded-xl bg-white px-4 py-3.5 text-left transition ${on ? 'ring-2 ring-brand-500' : 'ring-1 ring-gray-200 hover:ring-gray-300'}`}>
              <span className="flex flex-wrap items-center justify-between gap-2">
                <span className={`text-[15px] font-semibold ${on ? 'text-brand-700' : l.offered ? 'text-gray-900' : 'text-gray-500'}`}>{l.name}</span>
                {l.offered
                  ? <Pill tone="green">Nas propostas</Pill>
                  : l.status === 'ATIVO' ? <Pill tone="amber">Fora das propostas</Pill> : <Pill tone="gray">Inativa</Pill>}
              </span>
              <span className="text-[13px] text-gray-500">
                {nTiers ? `${nTiers} modalidade${nTiers > 1 ? 's' : ''}` : 'sem modalidades'} · {nItems ? `${nItems} cobertura${nItems > 1 ? 's' : ''}` : 'sem coberturas'}
                {l.problems.length > 0 && <span className="text-amber-700"> · {l.problems.length} {l.problems.length > 1 ? 'atenções' : 'atenção'}</span>}
              </span>
            </button>
          )
        })}
      </div>

      {line && (
        <div className="space-y-4">
          {line.problems.length > 0 && (
            <div role="status" className={`flex gap-3 rounded-xl px-4 py-3 text-sm ring-1 ${line.offered ? 'bg-amber-50 text-amber-900 ring-amber-200' : 'bg-rose-50 text-rose-900 ring-rose-200'}`}>
              <AlertTriangle size={18} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">{line.offered ? 'Pontos de atenção' : 'Esta linha não aparece nas propostas porque:'}</p>
                <ul className="mt-1 list-disc pl-5">{line.problems.map(p => <li key={p}>{p}</li>)}</ul>
              </div>
            </div>
          )}

          <section className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
            <SectionHeader
              title={<span className="flex flex-wrap items-center gap-2">
                Modalidades da linha {line.name}
                {canEdit && <RenameButton onClick={() => setRenaming({ type: 'line', id: line.id, name: line.name, history: line.nameHistory, what: 'a linha' })} />}
              </span>}
              description={<>Preço cheio da tabela ativa{meta?.activeTable ? ` (v${meta.activeTable.version})` : ''}. Para mudar valores, importe uma planilha em Tabelas de preço.{line.description ? <> {line.description}</> : null}</>}
              actions={line.offered ? <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 size={16} /> Aparece no assistente de proposta</span> : undefined}
            />
            <div className="-mt-2 px-6 pb-3"><HistoryNote history={line.nameHistory} /></div>
            <div className="border-t border-gray-200">
              <FixedTable columns={tierCols} rows={line.tiers} getRowKey={t => t.id}
                emptyMessage="Nenhuma modalidade. Cadastre em Cadastros ou importe a planilha." minWidth={860} />
            </div>
          </section>

          <section className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
            <SectionHeader
              title="Coberturas e benefícios"
              description="Na ordem do PDF. O peso de cada cobertura vem da tabela ativa; sem peso em nenhuma modalidade, ela não entra na proposta."
              actions={canCreate && line.available.length > 0 ? <>
                <div className="w-64">
                  <Select value={adding} onChange={e => setAdding(e.target.value)} placeholder="Incluir cobertura"
                    options={line.available.map(a => ({ value: a.id, label: a.name }))} searchPlaceholder="Buscar cobertura" />
                </div>
                <Button onClick={addItem} disabled={!adding} loading={busy && !!adding} iconLeft={<Plus size={15} />}>Incluir</Button>
              </> : undefined}
            />
            <div className="border-t border-gray-200">
              <FixedTable columns={itemCols} rows={line.items} getRowKey={i => i.id}
                detail={i => (exp.isOpen(i.id) && (i.description || i.generalText)) ? (
                  <TextPanel html={i.description || i.generalText}
                    aside={<>
                      <p className="text-xs text-gray-500">{i.description ? 'Texto desta cobertura na linha.' : 'Texto geral da cobertura (esta linha não tem um próprio).'}</p>
                      {canEdit && <Button size="sm" variant="secondary" iconLeft={<Pencil size={14} />} onClick={() => setEditing(i)}>Editar textos</Button>}
                    </>}>
                    {i.tierTexts.length > 0 && (
                      <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Por modalidade</p>
                        {i.tierTexts.map(tt => (
                          <div key={tt.tierId} className="flex flex-wrap gap-x-3">
                            <span className="w-28 shrink-0 font-semibold text-gray-900">{line.tiers.find(t => t.id === tt.tierId)?.name}</span>
                            <RichHtml html={tt.text} className="min-w-0 flex-1" />
                          </div>
                        ))}
                      </div>
                    )}
                  </TextPanel>
                ) : null}
                rowClassName={i => (i.itemStatus !== 'ATIVO' ? 'bg-amber-50/50' : undefined)}
                emptyMessage="Nenhuma cobertura nesta linha."
                minWidth={380 + (canEdit ? 88 : 0) + 112 + 112 + line.tiers.length * 104 + 104}
                footer={line.items.length > 0 ? weightFooter : undefined} />
            </div>
          </section>
        </div>
      )}

      {editing && line && (
        <EditItemModal key={editing.id} item={editing} line={line} busy={busy} onClose={() => setEditing(null)}
          onSave={async payload => { if (await run(() => Service.updateItem(editing.id, payload), 'Textos salvos.')) setEditing(null) }} />
      )}

      {renaming && (
        <RenameModal key={`${renaming.type}${renaming.id}`} target={renaming} busy={busy} onClose={() => setRenaming(null)}
          onSave={async name => { if (await run(() => Service.rename(renaming.type, renaming.id, name), 'Nome alterado.')) setRenaming(null) }} />
      )}

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={async () => { if (removing && await run(() => Service.removeItem(removing.id), 'Cobertura retirada da linha.')) setRemoving(null) }}
        title="Tirar a cobertura da linha?"
        description={removing ? `"${removing.name}" deixa de aparecer nas próximas propostas da linha ${line?.name}. ${usage(removing.itemId) > 0 ? `As ${usage(removing.itemId)} proposta(s) que já a usam não mudam.` : ''}` : ''}
        confirmLabel="Tirar da linha"
        variant="danger"
        loading={busy}
      />
    </>
  )
}

function RenameButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} title="Renomear" aria-label="Renomear"
      className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-brand-600">
      <Pencil size={12} />
    </button>
  )
}

function HistoryNote({ history }: { history: NameHistoryEntry[] }) {
  if (!history?.length) return null
  const last = history[history.length - 1]
  return (
    <p className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500" title={history.map(h => `${h.name} (até ${new Date(h.until).toLocaleDateString('pt-BR')})`).join('\n')}>
      <History size={11} /> antes: {last.name}{history.length > 1 ? ` (+${history.length - 1})` : ''}
    </p>
  )
}

function EditItemModal({ item, line, busy, onClose, onSave }: {
  item: CatalogLineItem
  line: CatalogLine
  busy: boolean
  onClose: () => void
  onSave: (p: { description: string | null; gracePeriod: string | null; tierTexts: { tierId: string; description: string }[] }) => void
}) {
  const [description, setDescription] = useState(item.description ?? '')
  const [grace, setGrace] = useState(item.grace ?? '')
  const [tierTexts, setTierTexts] = useState<Record<string, string>>(
    () => Object.fromEntries(item.tierTexts.map(t => [t.tierId, t.text])),
  )
  const empty = !richToPlain(description)

  return (
    <Modal open onClose={onClose} size="lg" title={`${item.name} · linha ${line.name}`}
      description="Como a cobertura aparece no PDF das propostas desta linha. Propostas já criadas não mudam."
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button loading={busy} onClick={() => onSave({
          description: empty ? null : description,
          gracePeriod: grace.trim() || null,
          tierTexts: line.tiers.map(t => ({ tierId: t.id, description: tierTexts[t.id] ?? '' })).filter(t => richToPlain(t.description)),
        })}>Salvar</Button>
      </>}>
      <div className="space-y-5">
        <div>
          <p className="mb-1.5 text-sm font-medium text-gray-700">Texto nesta linha</p>
          <RichTextEditor value={description} onChange={setDescription} />
          <p className="mt-1 text-xs text-gray-500">
            {item.generalText ? <>Vazio = usa o texto geral: “{richToPlain(item.generalText).slice(0, 140)}{richToPlain(item.generalText).length > 140 ? '…' : ''}”</> : 'Vazio = a cobertura aparece só com o nome.'}
          </p>
        </div>
        <Input label="Carência" value={grace} onChange={e => setGrace(e.target.value)} placeholder="Ex.: 15 dias, imediata"
          hint="Aparece ao lado da cobertura no PDF. Deixe vazio se não houver." />
        {line.tiers.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700">Texto por modalidade <span className="font-normal text-gray-500">(opcional)</span></p>
            <p className="mb-2 text-xs text-gray-500">Quando muda de uma modalidade para outra — por exemplo, o capital de cada nível do Ouro.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {line.tiers.map(t => (
                <RichTextEditor key={t.id} compact label={t.name} value={tierTexts[t.id] ?? ''}
                  onChange={html => setTierTexts(s => ({ ...s, [t.id]: html }))} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function RenameModal({ target, busy, onClose, onSave }: {
  target: RenameTarget
  busy: boolean
  onClose: () => void
  onSave: (name: string) => void
}) {
  const [name, setName] = useState(target.name)
  const changed = name.trim() !== '' && name.trim() !== target.name

  return (
    <Modal open onClose={onClose} size="md" title={`Renomear ${target.what}`}
      description="O novo nome vale para as próximas propostas. As que já existem continuam com o nome de quando foram salvas."
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button loading={busy} disabled={!changed} onClick={() => onSave(name.trim())}>Renomear</Button>
      </>}>
      <div className="space-y-4">
        <Input label="Nome" value={name} onChange={e => setName(e.target.value)} autoFocus maxLength={150} />
        {target.history.length > 0 && (
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-gray-700"><History size={14} /> Nomes anteriores</p>
            <ul className="space-y-1 text-sm text-gray-600">
              {[...target.history].reverse().map((h, k) => (
                <li key={k} className="flex justify-between gap-3 rounded bg-gray-50 px-2.5 py-1.5">
                  <span>{h.name}</span><span className="text-xs text-gray-500">até {new Date(h.until).toLocaleDateString('pt-BR')}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-xs text-gray-500">Na importação de planilha, o nome antigo continua sendo reconhecido.</p>
      </div>
    </Modal>
  )
}
